import { useState } from 'react'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { relativeTime } from '@/lib/format'
import { Button } from '@/ui/Button'
import { TextField } from '@/ui/Field'
import { Modal } from '@/ui/Modal'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

type Endpoint = Schema<'EndpointOut'>
type Delivery = Schema<'DeliveryOut'>

/**
 * Outbound webhooks, on the Notifications panel.
 *
 * S-D3 designed eight settings panels and webhooks is not one of them, so this
 * is a **second card on an approved panel** rather than a ninth entry in the
 * approved navigation — the alternative being an API a tenant cannot reach,
 * which is worse than either. Everything here is S-D1 components: the card, the
 * table, the field, the modal.
 *
 * **The secret is shown on demand, more than once.** It is derived, never
 * stored (`app/notifications/webhooks.py`), so there is nothing to reveal once
 * and lose; a show-once affordance would only mean a tenant who mislaid it had
 * to rotate, which invalidates every delivery already queued. The guard that
 * matters is the permission on the route.
 *
 * **The delivery log is what makes a failure legible.** Every attempt is a row
 * with its status code, the receiver's own message and when the next retry is
 * due — which is what the session's exit criterion means by "visible in the
 * delivery log", and what turns "our webhooks stopped" into a screenshot.
 */

const STATE_TONE: Record<string, string> = {
  delivered: 'ar-badge--success',
  pending: 'ar-badge--info',
  parked: 'ar-badge--danger',
}

export function WebhooksCard(): JSX.Element {
  const toast = useToast()
  const now = new Date()
  const [adding, setAdding] = useState(false)
  const [url, setUrl] = useState('')
  const [description, setDescription] = useState('')
  const [revealed, setRevealed] = useState<Record<string, string>>({})
  const [confirmDelete, setConfirmDelete] = useState<Endpoint | null>(null)

  const endpoints = useQuery((signal) => call(() => api.GET('/api/v1/webhooks', { signal })), [])
  const deliveries = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/webhooks/deliveries', { params: { query: { limit: 20 } }, signal }),
      ),
    [],
  )

  const create = useMutation((body: { url: string; description: string }) =>
    call(() =>
      api.POST('/api/v1/webhooks', {
        body: { url: body.url, description: body.description || null, events: [] },
      }),
    ),
  )
  const patch = useMutation((body: { id: string; enabled: boolean }) =>
    call(() =>
      api.PATCH('/api/v1/webhooks/{endpoint_id}', {
        params: { path: { endpoint_id: body.id } },
        body: { enabled: body.enabled },
      }),
    ),
  )
  const remove = useMutation((id: string) =>
    call(() =>
      api.DELETE('/api/v1/webhooks/{endpoint_id}', { params: { path: { endpoint_id: id } } }),
    ),
  )
  const reveal = useMutation((id: string) =>
    call(() =>
      api.GET('/api/v1/webhooks/{endpoint_id}/secret', { params: { path: { endpoint_id: id } } }),
    ),
  )
  const rotate = useMutation((id: string) =>
    call(() =>
      api.POST('/api/v1/webhooks/{endpoint_id}/rotate-secret', {
        params: { path: { endpoint_id: id } },
      }),
    ),
  )
  const retry = useMutation((id: number) =>
    call(() =>
      api.POST('/api/v1/webhooks/deliveries/{delivery_id}/retry', {
        params: { path: { delivery_id: id } },
      }),
    ),
  )

  const rows = endpoints.state.data?.endpoints ?? []
  const log = deliveries.state.data?.deliveries ?? []

  function refreshAll(): void {
    endpoints.refetch()
    deliveries.refetch()
  }

  return (
    <>
      <section className="ar-card">
        <div className="ar-card__header">
          <h3>Webhooks</h3>
          <span className="ar-card__spacer" />
          <Button
            tone="ghost"
            size="sm"
            onClick={() => {
              create.reset()
              setUrl('')
              setDescription('')
              setAdding(true)
            }}
          >
            Add endpoint
          </Button>
        </div>

        <InlineError
          failure={
            patch.state.failure ??
            remove.state.failure ??
            reveal.state.failure ??
            rotate.state.failure
          }
        />

        {endpoints.state.status === 'loading' ? (
          <div className="ar-card__body">
            <Loading label="Loading webhook endpoints" lines={2} />
          </div>
        ) : null}

        {endpoints.state.status === 'error' ? (
          <ErrorState
            failure={endpoints.state.failure}
            title="Could not load your webhook endpoints"
            onRetry={endpoints.refetch}
          />
        ) : null}

        {endpoints.state.status === 'ready' && rows.length === 0 ? (
          <div className="ar-card__body">
            <Empty
              icon="↗"
              title="No webhook endpoints"
              body="Add one and AugRank will POST every notification to it, signed, with bounded retries."
            />
          </div>
        ) : null}

        {endpoints.state.status === 'ready' && rows.length > 0 ? (
          <div className="ar-table-wrap">
            <table className="ar-table">
              <thead>
                <tr>
                  <th>Endpoint</th>
                  <th>Status</th>
                  <th>Last delivery</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <strong className="ar-mono">{row.url}</strong>
                      {row.description ? (
                        <div
                          className="ar-subtle"
                          style={{ fontSize: 'var(--ar-text-caption-size)' }}
                        >
                          {row.description}
                        </div>
                      ) : null}
                      {revealed[row.id] ? (
                        <div className="ar-mono" style={{ marginTop: 'var(--ar-space-2)' }}>
                          {revealed[row.id]}
                        </div>
                      ) : null}
                    </td>
                    <td>
                      {row.enabled ? (
                        <span className="ar-badge ar-badge--success">Enabled</span>
                      ) : (
                        <span className="ar-badge ar-badge--danger">Off</span>
                      )}
                      {row.disabled_reason ? (
                        <div
                          className="ar-subtle"
                          style={{ fontSize: 'var(--ar-text-caption-size)' }}
                        >
                          {row.disabled_reason}
                        </div>
                      ) : null}
                    </td>
                    <td className="ar-muted">
                      {row.last_delivery_at ? relativeTime(row.last_delivery_at, now) : 'Never'}
                    </td>
                    <td className="ar-num">
                      <Button
                        tone="quiet"
                        size="sm"
                        onClick={() => {
                          void (async () => {
                            const result = await reveal.run(row.id)
                            if (result.ok) {
                              setRevealed((current) => ({
                                ...current,
                                [row.id]: result.data.secret,
                              }))
                            }
                          })()
                        }}
                      >
                        Show secret
                      </Button>{' '}
                      <Button
                        tone="quiet"
                        size="sm"
                        onClick={() => {
                          void (async () => {
                            const result = await rotate.run(row.id)
                            if (!result.ok) return
                            // Filtered rather than deleted: the revealed secret
                            // is no longer the endpoint's after a rotation, and
                            // leaving it on screen would show a value that no
                            // longer verifies anything.
                            setRevealed((current) =>
                              Object.fromEntries(
                                Object.entries(current).filter(([id]) => id !== row.id),
                              ),
                            )
                            refreshAll()
                            toast.show({
                              kind: 'success',
                              title: 'Signing secret rotated',
                              body: 'Deliveries already queued keep the old one.',
                            })
                          })()
                        }}
                      >
                        Rotate
                      </Button>{' '}
                      <Button
                        tone="quiet"
                        size="sm"
                        onClick={() => {
                          void (async () => {
                            const result = await patch.run({ id: row.id, enabled: !row.enabled })
                            if (result.ok) refreshAll()
                          })()
                        }}
                      >
                        {row.enabled ? 'Disable' : 'Enable'}
                      </Button>{' '}
                      <Button
                        tone="quiet"
                        size="sm"
                        onClick={() => {
                          setConfirmDelete(row)
                        }}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        <div className="ar-card__footer">
          <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
            Every delivery carries <span className="ar-mono">X-AugRank-Signature</span>:{' '}
            <span className="ar-mono">
              t=&lt;unix&gt;,v1=HMAC-SHA256(secret, &quot;t.body&quot;)
            </span>
            . Verify it before you trust the body.
          </span>
        </div>
      </section>

      <section className="ar-card">
        <div className="ar-card__header">
          <h3>Delivery log</h3>
          <span className="ar-card__spacer" />
          <Button
            tone="quiet"
            size="sm"
            onClick={() => {
              deliveries.refetch()
            }}
          >
            Refresh
          </Button>
        </div>

        <InlineError failure={retry.state.failure} />

        {deliveries.state.status === 'loading' ? (
          <div className="ar-card__body">
            <Loading label="Loading the delivery log" lines={3} />
          </div>
        ) : null}

        {deliveries.state.status === 'error' ? (
          <ErrorState
            failure={deliveries.state.failure}
            title="Could not load the delivery log"
            onRetry={deliveries.refetch}
          />
        ) : null}

        {deliveries.state.status === 'ready' && log.length === 0 ? (
          <div className="ar-card__body">
            <Empty
              icon="○"
              title="Nothing delivered yet"
              body="Attempts appear here as they happen, with the status code and the next retry."
            />
          </div>
        ) : null}

        {deliveries.state.status === 'ready' && log.length > 0 ? (
          <div className="ar-table-wrap">
            <table className="ar-table">
              <thead>
                <tr>
                  <th>Event</th>
                  <th>State</th>
                  <th>Attempts</th>
                  <th>Result</th>
                  <th>When</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {log.map((row: Delivery) => (
                  <tr key={row.id}>
                    <td>
                      <strong>{row.event}</strong>
                      <div
                        className="ar-subtle ar-mono"
                        style={{ fontSize: 'var(--ar-text-caption-size)' }}
                      >
                        {row.url}
                      </div>
                    </td>
                    <td>
                      <span className={`ar-badge ${STATE_TONE[row.state] ?? ''}`}>{row.state}</span>
                    </td>
                    <td className="ar-num">
                      {row.attempts} / {row.max_attempts}
                    </td>
                    <td>
                      {row.last_status_code !== null ? (
                        <span className="ar-mono">{row.last_status_code}</span>
                      ) : null}
                      {row.last_error ? (
                        <div
                          className="ar-subtle"
                          style={{ fontSize: 'var(--ar-text-caption-size)' }}
                        >
                          {row.last_error}
                        </div>
                      ) : null}
                    </td>
                    <td className="ar-muted">
                      {row.state === 'pending'
                        ? `Next ${relativeTime(row.next_attempt_at, now)}`
                        : relativeTime(row.delivered_at ?? row.created_at, now)}
                    </td>
                    <td className="ar-num">
                      {row.state === 'parked' ? (
                        <Button
                          tone="quiet"
                          size="sm"
                          onClick={() => {
                            void (async () => {
                              const result = await retry.run(row.id)
                              if (!result.ok) return
                              deliveries.refetch()
                              toast.show({ kind: 'success', title: 'Queued for another attempt' })
                            })()
                          }}
                        >
                          Retry
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      {adding ? (
        <Modal
          title="Add a webhook endpoint"
          onClose={() => {
            setAdding(false)
          }}
          footer={
            <>
              <Button
                tone="ghost"
                onClick={() => {
                  setAdding(false)
                }}
              >
                Cancel
              </Button>
              <Button
                tone="primary"
                busy={create.pending}
                busyLabel="Adding"
                onClick={() => {
                  void (async () => {
                    const result = await create.run({ url, description })
                    if (!result.ok) return
                    setAdding(false)
                    refreshAll()
                    toast.show({
                      kind: 'success',
                      title: 'Endpoint added',
                      body: 'Show its secret to verify the signature on every delivery.',
                    })
                  })()
                }}
              >
                Add endpoint
              </Button>
            </>
          }
        >
          <InlineError failure={create.state.failure} />
          <TextField
            label="URL"
            type="url"
            value={url}
            required
            placeholder="https://example.com/augrank"
            help="https only. AugRank fetches this from its own servers, so an address on a private network is refused."
            onChange={(event) => {
              setUrl(event.currentTarget.value)
            }}
          />
          <TextField
            label="Description"
            value={description}
            placeholder="Our CRM"
            help="Optional. Shown in this list so you can tell two endpoints apart."
            onChange={(event) => {
              setDescription(event.currentTarget.value)
            }}
          />
        </Modal>
      ) : null}

      {confirmDelete ? (
        <Modal
          title="Remove this endpoint?"
          onClose={() => {
            setConfirmDelete(null)
          }}
          footer={
            <>
              <Button
                tone="ghost"
                onClick={() => {
                  setConfirmDelete(null)
                }}
              >
                Keep it
              </Button>
              <Button
                tone="danger"
                busy={remove.pending}
                busyLabel="Removing"
                onClick={() => {
                  const target = confirmDelete
                  void (async () => {
                    const result = await remove.run(target.id)
                    if (!result.ok) return
                    setConfirmDelete(null)
                    refreshAll()
                    toast.show({ kind: 'success', title: 'Endpoint removed' })
                  })()
                }}
              >
                Remove
              </Button>
            </>
          }
        >
          <p style={{ marginTop: 0 }}>
            AugRank will stop posting to <span className="ar-mono">{confirmDelete.url}</span>. Its
            delivery history stays, so you can still see what was sent.
          </p>
        </Modal>
      ) : null}
    </>
  )
}
